import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useSeo } from '../lib/useSeo';
import { celebrate } from '../lib/celebrate';
import { CalendarClock, Loader2, CheckCircle2, XCircle } from 'lucide-react';

// Module-level guard: survives React StrictMode's double-mount so a single-use
// token is only POSTed once per page load (dev remounts share this module).
const _processedTokens = new Map(); // token -> Promise<'success'|'error'>

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const { user, checkAuth } = useAuth();
  useSeo({ title: 'Verify your email · Larkwell', description: 'Confirm your Larkwell email address.', canonical: 'https://larkwellapp.com/verify-email' });

  const [status, setStatus] = useState('loading'); // loading | success | error

  useEffect(() => {
    let alive = true;
    if (!token) { setStatus('error'); return; }
    if (!_processedTokens.has(token)) {
      _processedTokens.set(token, api.post('/auth/verify', { token })
        .then(() => 'success')
        .catch(() => 'error'));
    }
    _processedTokens.get(token).then(async (result) => {
      if (!alive) return;
      setStatus(result);
      if (result === 'success') {
        try { localStorage.setItem('larkwell_verified_celebrate', '1'); } catch (e) {}
        setTimeout(() => celebrate(), 250);
        try { await checkAuth(); } catch (e) {}
      }
    });
    return () => { alive = false; };
  }, [token, checkAuth]);

  const goNext = () => navigate(user ? '/app/today' : '/login');

  return (
    <div className="min-h-dvh grid place-items-center bg-[hsl(var(--background))] p-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-sm text-center">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="h-8 w-8 rounded-xl grid place-items-center" style={{ background: 'hsl(var(--primary))' }}><CalendarClock className="h-4 w-4 text-white" /></div>
          <span className="font-display text-lg font-700">Larkwell</span>
        </Link>

        {status === 'loading' && (
          <div data-testid="verify-loading">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-[hsl(var(--primary))]" />
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-4">Confirming your email…</p>
          </div>
        )}

        {status === 'success' && (
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-8" data-testid="verify-success">
            <div className="h-14 w-14 rounded-2xl grid place-items-center mx-auto mb-4" style={{ background: 'hsl(var(--accent))' }}><CheckCircle2 className="h-7 w-7" style={{ color: 'hsl(var(--success))' }} /></div>
            <h1 className="font-display text-xl font-700">You’re all set!</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1.5">Your email is verified. Welcome aboard — let’s make today gentle and doable.</p>
            <Button className="w-full mt-6 h-11 rounded-xl" style={{ background: 'hsl(var(--primary))' }} onClick={goNext} data-testid="verify-continue-button">{user ? 'Go to Larkwell' : 'Sign in'}</Button>
          </div>
        )}

        {status === 'error' && (
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-8" data-testid="verify-error">
            <div className="h-14 w-14 rounded-2xl grid place-items-center mx-auto mb-4" style={{ background: 'hsl(var(--destructive) / 0.12)' }}><XCircle className="h-7 w-7" style={{ color: 'hsl(var(--destructive))' }} /></div>
            <h1 className="font-display text-xl font-700">Link expired or invalid</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1.5">This verification link didn’t work. You can request a fresh one from inside Larkwell (look for the banner at the top).</p>
            <Button className="w-full mt-6 h-11 rounded-xl" style={{ background: 'hsl(var(--primary))' }} onClick={goNext} data-testid="verify-continue-button">{user ? 'Go to Larkwell' : 'Sign in'}</Button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
